/**
 * `/oms/customers/:id` — per-customer operator detail (OCP-150/151/152).
 *
 * The page is a card stack — each card pulls from the same single
 * `GET /v1/admin/oms/customers/:id` fetch so OMS only sees one round-
 * trip per page load. Mutations (quota edit, key revoke, force-verify,
 * suppression delete) re-fetch the parent on success so every card
 * reflects the latest state.
 *
 * Cards (top-to-bottom):
 *   1. Header (OCP-150)
 *   2. Quota + edit modal (OCP-150)
 *   3. API keys + per-row revoke (OCP-151)
 *   4. OAuth connections (OCP-151) — read-only mirror; revoke goes
 *      through the existing OCP-side portal_oauth_tokens revoke
 *   5. Sender domains + per-row force-verify (OCP-151)
 *   6. Recent sends (top 20) + link to /oms/sends?customer=:id (OCP-152)
 *   7. Suppression (top 20) + per-row remove + link to
 *      /oms/suppression?customer=:id (OCP-152)
 *
 * OMS response shape is treated as forgiving — every field nullable.
 * Cards render `—` when data is absent so an OMS-side change to which
 * fields are populated doesn't break the page.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, ApiError } from '../../lib/api';
import { fmtDateTime } from '../../lib/format-time';

interface CustomerDetail {
  id: string;
  email: string;
  portal_customer_id?: string | null;
  plan_tier?: string | null;
  status?: string | null;
  created_at?: string | null;
  quota?: {
    base_quota?: number;
    used?: number;
    allow_overage?: boolean;
    overage_cap_percent?: number;
    cap_reason?: string | null;
  } | null;
  /** OCP-621 - bounces / complaints this period against the SES bars (what the customer sees). */
  deliverability?: {
    reported: boolean;
    bounces: number | null;
    complaints: number | null;
    bounce_rate_pct: number | null;
    complaint_rate_pct: number | null;
    level: 'ok' | 'warning' | 'critical' | 'unknown';
    binding: 'complaints' | 'bounces' | null;
    bars: { complaint_warning_pct: number; complaint_critical_pct: number; bounce_warning_pct: number; bounce_critical_pct: number };
  } | null;
  api_keys?: Array<{
    id: string;
    prefix?: string;
    label?: string | null;
    source?: string | null;
    last_used_at?: string | null;
    revoked_at?: string | null;
    created_at?: string | null;
  }> | null;
  connections?: Array<{
    id: string;
    instance_url?: string | null;
    instance_label?: string | null;
    granted_by_user?: string | null;
    granted_at?: string | null;
    revoked_at?: string | null;
  }> | null;
  sender_domains?: Array<{
    id: string;
    domain: string;
    status?: string | null;
    dkim_mode?: string | null;
    last_verified_at?: string | null;
    /** OCP-219 — per-domain open-tracking opt-in. */
    open_tracking_enabled?: boolean | null;
    /**
     * OCP-612 / OMS-113 - Return-Path (SES custom MAIL FROM) state per
     * region + combined `state`. "DKIM verified but Return-Path
     * not-configured" is the spam-placement signature; Recheck repairs it.
     */
    mail_from_status?: {
      primary?: string | null;
      failover?: string | null;
      state?: string | null;
    } | null;
  }> | null;
  recent_sends?: Array<{
    id: string;
    subject?: string | null;
    from?: string | null;
    to?: string | null;
    status?: string | null;
    region?: string | null;
    sent_at?: string | null;
  }> | null;
  suppression?: Array<{
    id: string;
    email?: string | null;
    reason?: string | null;
    added_at?: string | null;
    added_by?: string | null;
  }> | null;
}

export function OmsCustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CustomerDetail | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error' | 'unconfigured'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setState('loading');
    setErrorDetail(null);
    try {
      const r = await api<CustomerDetail>(`/v1/admin/oms/customers/${id}`);
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setState('unconfigured');
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setErrorDetail(
          body?.detail
            ? `${body.error}: ${body.detail.slice(0, 200)}`
            : body?.error ?? `HTTP ${err.status}`,
        );
      } else {
        setErrorDetail(err instanceof Error ? err.message : 'Unknown error');
      }
      setState('error');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runAction<T>(fn: () => Promise<T>): Promise<boolean> {
    setActionError(null);
    try {
      await fn();
      await load();
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setActionError(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setActionError(err instanceof Error ? err.message : 'Action failed');
      }
      return false;
    }
  }

  if (state === 'unconfigured') {
    return <UnconfiguredEmptyState />;
  }
  if (state === 'error') {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
        <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">Could not load customer</h2>
        {errorDetail && <p className="mt-1 break-all text-xs text-red-800 dark:text-red-300">{errorDetail}</p>}
        <button
          type="button"
          onClick={() => void load()}
          className="mt-2 rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
        >
          Retry
        </button>
      </div>
    );
  }
  if (state === 'loading' || !data) {
    return <p className="text-sm text-gray-500">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumb email={data.email} />
      {actionError && (
        <div className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </div>
      )}
      <HeaderCard customer={data} />
      <QuotaCard
        quota={data.quota ?? null}
        onSave={async (patch) =>
          runAction(() =>
            api(`/v1/admin/oms/customers/${data.id}/quota`, {
              method: 'PATCH',
              body: patch,
            }),
          )
        }
      />
      <DeliverabilityCard deliverability={data.deliverability ?? null} customerId={data.id} />
      <ApiKeysCard
        keys={data.api_keys ?? null}
        onRevoke={async (keyId) =>
          runAction(() =>
            api(`/v1/admin/oms/customers/${data.id}/api-keys/${keyId}/revoke`, {
              method: 'POST',
            }),
          )
        }
      />
      <ConnectionsCard connections={data.connections ?? null} />
      <SenderDomainsCard
        domains={data.sender_domains ?? null}
        onForceVerify={async (domainId) =>
          runAction(() =>
            api(`/v1/admin/oms/sender-domains/${domainId}/force-verify`, {
              method: 'POST',
            }),
          )
        }
        onRecheck={async (domainId) =>
          runAction(() =>
            api(`/v1/admin/oms/customers/${data.id}/sender-domains/${domainId}/verify`, {
              method: 'POST',
            }),
          )
        }
        onSetOpenTracking={async (domainId, enabled) =>
          runAction(() =>
            api(`/v1/admin/oms/customers/${data.id}/sender-domains/${domainId}`, {
              method: 'PATCH',
              body: { open_tracking_enabled: enabled },
            }),
          )
        }
      />
      <RecentSendsCard sends={data.recent_sends ?? null} customerId={data.id} />
      <SuppressionCard
        items={data.suppression ?? null}
        customerId={data.id}
        onRemove={async (suppressionId) =>
          runAction(() =>
            api(`/v1/admin/oms/suppression/${suppressionId}`, { method: 'DELETE' }),
          )
        }
      />
    </div>
  );
}

// -------- cards --------

function Breadcrumb({ email }: { email: string }) {
  return (
    <div className="text-xs text-gray-500">
      <Link to="/oms/customers" className="text-brand-500 hover:underline">
        orboto Mail · Customers
      </Link>{' '}
      / <span className="text-gray-700 dark:text-gray-300">{email}</span>
    </div>
  );
}

function CardShell({
  title,
  subtitle,
  children,
  right,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        {right}
      </header>
      <div className="px-4 py-3 text-sm">{children}</div>
    </section>
  );
}

function HeaderCard({ customer }: { customer: CustomerDetail }) {
  return (
    <CardShell title="Customer">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Row label="Email" value={customer.email} />
        <Row
          label="Portal customer"
          value={
            customer.portal_customer_id ? (
              <a
                href={`/accounts/${customer.portal_customer_id}`}
                className="text-brand-500 hover:underline"
              >
                {customer.portal_customer_id.slice(0, 8)}…
              </a>
            ) : (
              '—'
            )
          }
        />
        <Row label="Plan tier" value={customer.plan_tier ?? '—'} />
        <Row label="Status" value={<StatusPill status={customer.status ?? null} />} />
        <Row label="orboto Mail customer ID" value={<code className="text-xs">{customer.id}</code>} />
        <Row
          label="Created"
          value={customer.created_at ? formatDate(customer.created_at) : '—'}
        />
      </dl>
    </CardShell>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900 dark:text-gray-100">{value}</dd>
    </>
  );
}

/** OCP-621 - the operator sees what the customer sees on /mail. */
function DeliverabilityCard({ deliverability, customerId }: { deliverability: NonNullable<CustomerDetail['deliverability']> | null; customerId: string }) {
  const d = deliverability;
  const tone =
    d?.level === 'critical' ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 ring-red-200 dark:ring-red-800'
    : d?.level === 'warning' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 ring-amber-200 dark:ring-amber-800'
    : d?.level === 'ok' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800'
    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-200 dark:ring-gray-700';
  const label = d?.level === 'critical' ? 'At risk' : d?.level === 'warning' ? 'Attention' : d?.level === 'ok' ? 'Healthy' : 'Not reported';
  const pct = (n: number | null | undefined) => (n == null ? '-' : `${n.toFixed(1)}%`);
  return (
    <CardShell
      title="Deliverability (this period)"
      subtitle="Bounces + complaints against the SES bars - the same reading the customer sees on /mail."
      right={<span data-testid="oms-deliverability-level" className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone}`}>{label}</span>}
    >
      {!d || !d.reported ? (
        <p className="text-gray-500">The mail service does not report bounce / complaint counters for this customer (pre OMS-116).</p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-700 dark:text-gray-300 sm:grid-cols-4" data-testid="oms-deliverability">
          <span>Complaints: <strong>{d.complaints ?? 0}</strong> ({pct(d.complaint_rate_pct)})</span>
          <span>Bounces: <strong>{d.bounces ?? 0}</strong> ({pct(d.bounce_rate_pct)})</span>
          <span>Bars: complaint {d.bars.complaint_warning_pct}% / {d.bars.complaint_critical_pct}%</span>
          <span>bounce {d.bars.bounce_warning_pct}% / {d.bars.bounce_critical_pct}%</span>
          <span className="col-span-2 sm:col-span-4">
            <a className="text-brand-600 hover:underline" href={`/oms/sends?customer=${encodeURIComponent(customerId)}&status=complained`}>Complained sends</a>
            {' · '}
            <a className="text-brand-600 hover:underline" href={`/oms/suppression?customer=${encodeURIComponent(customerId)}&reason=complaint`}>Complaint suppressions</a>
          </span>
        </dl>
      )}
    </CardShell>
  );
}

function QuotaCard({
  quota,
  onSave,
}: {
  quota: NonNullable<CustomerDetail['quota']> | null;
  onSave: (patch: {
    base_quota?: number;
    allow_overage?: boolean;
    overage_cap_percent?: number;
  }) => Promise<boolean>;
}) {
  const [edit, setEdit] = useState(false);
  return (
    <CardShell
      title="Quota"
      right={
        <button
          type="button"
          onClick={() => setEdit(true)}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          Edit quota
        </button>
      }
    >
      {quota ? (
        <div className="space-y-2">
          <QuotaBar used={quota.used ?? 0} total={quota.base_quota ?? 0} />
          <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-700 dark:text-gray-300">
            <span>Base: {quota.base_quota?.toLocaleString() ?? '—'}</span>
            <span>Used: {quota.used?.toLocaleString() ?? '—'}</span>
            <span>Overage: {quota.allow_overage ? 'allowed' : 'blocked'}</span>
            {quota.overage_cap_percent != null && (
              <span>Cap %: {quota.overage_cap_percent}</span>
            )}
            {quota.cap_reason && <span>Reason: {quota.cap_reason}</span>}
          </dl>
        </div>
      ) : (
        <p className="text-gray-500">No quota data.</p>
      )}
      {edit && (
        <QuotaEditModal
          quota={quota}
          onCancel={() => setEdit(false)}
          onSubmit={async (patch) => {
            const ok = await onSave(patch);
            if (ok) setEdit(false);
          }}
        />
      )}
    </CardShell>
  );
}

function QuotaBar({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const tone = pct >= 95 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
      <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function QuotaEditModal({
  quota,
  onCancel,
  onSubmit,
}: {
  quota: NonNullable<CustomerDetail['quota']> | null;
  onCancel: () => void;
  onSubmit: (p: {
    base_quota?: number;
    allow_overage?: boolean;
    overage_cap_percent?: number;
  }) => Promise<void>;
}) {
  const [baseQuota, setBaseQuota] = useState(String(quota?.base_quota ?? ''));
  const [allowOverage, setAllowOverage] = useState(Boolean(quota?.allow_overage));
  const [capPercent, setCapPercent] = useState(String(quota?.overage_cap_percent ?? ''));
  const [submitting, setSubmitting] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const patch: {
        base_quota?: number;
        allow_overage?: boolean;
        overage_cap_percent?: number;
      } = { allow_overage: allowOverage };
      if (baseQuota.trim() !== '') patch.base_quota = Number(baseQuota);
      if (capPercent.trim() !== '') patch.overage_cap_percent = Number(capPercent);
      await onSubmit(patch);
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <ModalWrap title="Adjust quota" onClose={onCancel}>
      <form onSubmit={submit} className="flex flex-col gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-600 dark:text-gray-400">Base quota (per month)</span>
          <input
            type="number"
            min={0}
            value={baseQuota}
            onChange={(e) => setBaseQuota(e.target.value)}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
          />
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={allowOverage}
            onChange={(e) => setAllowOverage(e.target.checked)}
            className="h-4 w-4 accent-brand-500"
          />
          <span className="text-sm">Allow overage</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-600 dark:text-gray-400">Overage cap %</span>
          <input
            type="number"
            min={0}
            max={1000}
            value={capPercent}
            onChange={(e) => setCapPercent(e.target.value)}
            disabled={!allowOverage}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 disabled:bg-gray-100 dark:disabled:bg-gray-800/60"
          />
        </label>
        <ModalFooter onCancel={onCancel} submitting={submitting} submitLabel="Save" />
      </form>
    </ModalWrap>
  );
}

function ApiKeysCard({
  keys,
  onRevoke,
}: {
  keys: NonNullable<CustomerDetail['api_keys']> | null;
  onRevoke: (id: string) => Promise<boolean>;
}) {
  const [confirming, setConfirming] = useState<{ id: string; label: string } | null>(null);
  return (
    <CardShell
      title="API keys"
      subtitle={keys ? `${keys.length} ${keys.length === 1 ? 'key' : 'keys'}` : undefined}
    >
      {!keys || keys.length === 0 ? (
        <p className="text-gray-500">No API keys.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1">Prefix</th>
              <th className="py-1">Label</th>
              <th className="py-1">Source</th>
              <th className="py-1">Last used</th>
              <th className="py-1">Status</th>
              <th className="py-1 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {keys.map((k) => (
              <tr key={k.id}>
                <td className="py-1.5 font-mono text-xs">{k.prefix ?? '—'}</td>
                <td className="py-1.5">{k.label ?? '—'}</td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">{k.source ?? '—'}</td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">
                  {k.last_used_at ? formatDate(k.last_used_at) : '—'}
                </td>
                <td className="py-1.5">
                  {k.revoked_at ? (
                    <span className="text-xs text-red-700 dark:text-red-300">revoked</span>
                  ) : (
                    <span className="text-xs text-emerald-700 dark:text-emerald-300">active</span>
                  )}
                </td>
                <td className="py-1.5 text-right">
                  {!k.revoked_at && (
                    <button
                      type="button"
                      onClick={() => setConfirming({ id: k.id, label: k.prefix ?? k.id })}
                      className="text-xs text-red-700 dark:text-red-300 hover:underline"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {confirming && (
        <ConfirmModal
          title="Revoke API key"
          message={`This immediately stops ${confirming.label} from authenticating with orboto Mail. Continue?`}
          confirmLabel="Revoke"
          confirmTone="red"
          onCancel={() => setConfirming(null)}
          onConfirm={async () => {
            const ok = await onRevoke(confirming.id);
            if (ok) setConfirming(null);
          }}
        />
      )}
    </CardShell>
  );
}

function ConnectionsCard({
  connections,
}: {
  connections: NonNullable<CustomerDetail['connections']> | null;
}) {
  return (
    <CardShell
      title="OAuth connections"
      subtitle={
        connections
          ? `${connections.length} ${connections.length === 1 ? 'connection' : 'connections'}`
          : undefined
      }
    >
      {!connections || connections.length === 0 ? (
        <p className="text-gray-500">No OAuth connections.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1">Instance</th>
              <th className="py-1">Label</th>
              <th className="py-1">Granted by</th>
              <th className="py-1">Granted</th>
              <th className="py-1">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {connections.map((c) => (
              <tr key={c.id}>
                <td className="py-1.5">{c.instance_url ?? '—'}</td>
                <td className="py-1.5">{c.instance_label ?? '—'}</td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">{c.granted_by_user ?? '—'}</td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">
                  {c.granted_at ? formatDate(c.granted_at) : '—'}
                </td>
                <td className="py-1.5">
                  {c.revoked_at ? (
                    <span className="text-xs text-red-700 dark:text-red-300">revoked</span>
                  ) : (
                    <span className="text-xs text-emerald-700 dark:text-emerald-300">active</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <p className="mt-3 text-xs text-gray-500">
        To revoke an OAuth connection, use the corresponding API-key revoke above
        — the connection record is auto-cascaded by orboto Mail.
      </p>
    </CardShell>
  );
}

function SenderDomainsCard({
  domains,
  onForceVerify,
  onRecheck,
  onSetOpenTracking,
}: {
  domains: NonNullable<CustomerDetail['sender_domains']> | null;
  onForceVerify: (id: string) => Promise<boolean>;
  /** OCP-612 - the customer's own verify (DKIM poll + MAIL FROM re-trigger). */
  onRecheck: (id: string) => Promise<boolean>;
  onSetOpenTracking: (id: string, enabled: boolean) => Promise<boolean>;
}) {
  const [confirming, setConfirming] = useState<{ id: string; domain: string } | null>(null);
  const [trackingBusy, setTrackingBusy] = useState<string | null>(null);
  const [recheckBusy, setRecheckBusy] = useState<string | null>(null);
  return (
    <CardShell
      title="Sender domains"
      subtitle={
        domains ? `${domains.length} ${domains.length === 1 ? 'domain' : 'domains'}` : undefined
      }
    >
      {!domains || domains.length === 0 ? (
        <p className="text-gray-500">No sender domains.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1">Domain</th>
              <th className="py-1">DKIM mode</th>
              <th className="py-1">Status</th>
              {/* OCP-612 - SES custom MAIL FROM per region; not-configured on a verified domain = spam placement. */}
              <th className="py-1" title="Return-Path (SES custom MAIL FROM, bounces.<domain>) - primary / failover region">
                Return-Path
              </th>
              {/* OCP-219 — operator override; drives the customer-portal toggle too. */}
              <th className="py-1" title="Operator override — drives the customer-portal toggle too">
                Open tracking
              </th>
              <th className="py-1">Last verified</th>
              <th className="py-1 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {domains.map((d) => (
              <tr key={d.id}>
                <td className="py-1.5 font-medium">{d.domain}</td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">{d.dkim_mode ?? '—'}</td>
                <td className="py-1.5">
                  <StatusPill status={d.status ?? null} />
                </td>
                <td className="py-1.5">
                  <ReturnPathCell status={d.mail_from_status ?? null} dkimStatus={d.status ?? null} />
                </td>
                <td className="py-1.5">
                  <label className="inline-flex cursor-pointer items-center gap-1.5">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-brand-500"
                      checked={d.open_tracking_enabled ?? false}
                      disabled={trackingBusy === d.id}
                      onChange={async (e) => {
                        const next = e.target.checked;
                        setTrackingBusy(d.id);
                        try {
                          await onSetOpenTracking(d.id, next);
                        } finally {
                          setTrackingBusy(null);
                        }
                      }}
                    />
                    <span className="text-xs text-gray-500">
                      {d.open_tracking_enabled ? 'on' : 'off'}
                    </span>
                  </label>
                </td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">
                  {d.last_verified_at ? formatDate(d.last_verified_at) : '—'}
                </td>
                <td className="py-1.5 text-right">
                  {/* OCP-612 - same call as the customer's Verify button: DKIM poll +
                      MAIL FROM re-trigger. Always offered; idempotent. */}
                  <button
                    type="button"
                    disabled={recheckBusy === d.id}
                    onClick={async () => {
                      setRecheckBusy(d.id);
                      try {
                        await onRecheck(d.id);
                      } finally {
                        setRecheckBusy(null);
                      }
                    }}
                    className="mr-3 text-xs text-brand-500 hover:underline disabled:opacity-50"
                    title="Re-run the customer's verify: polls DKIM in both regions and re-provisions the Return-Path if it is missing or failed"
                  >
                    {recheckBusy === d.id ? 'Rechecking…' : 'Recheck'}
                  </button>
                  {d.status !== 'verified' && (
                    <button
                      type="button"
                      onClick={() => setConfirming({ id: d.id, domain: d.domain })}
                      className="text-xs text-brand-500 hover:underline"
                    >
                      Force verify
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {confirming && (
        <ConfirmModal
          title="Force-verify sender domain"
          message={`Skip DNS propagation check and force ${confirming.domain} to verified state. This is an operator override — use only when DNS is correct but propagation is delayed. Continue?`}
          confirmLabel="Force verify"
          confirmTone="amber"
          onCancel={() => setConfirming(null)}
          onConfirm={async () => {
            const ok = await onForceVerify(confirming.id);
            if (ok) setConfirming(null);
          }}
        />
      )}
    </CardShell>
  );
}

/**
 * OCP-612 - Return-Path cell: combined chip + per-region detail. The
 * support signature "DKIM verified, Return-Path not configured" gets an
 * explicit hint so it is readable at a glance.
 */
function ReturnPathCell({
  status,
  dkimStatus,
}: {
  status: NonNullable<NonNullable<CustomerDetail['sender_domains']>[number]['mail_from_status']> | null;
  dkimStatus: string | null;
}) {
  const state = status?.state ?? 'unknown';
  const regions = status
    ? `${status.primary ?? 'n/a'} / ${status.failover ?? 'n/a'}`
    : 'not reported';
  const chip =
    state === 'success' ? 'verified'
      : state === 'failed' ? 'failed'
        : state === 'pending' || state === 'temporary-failure' ? 'pending'
          : state === 'not-configured' ? 'not configured'
            : null;
  return (
    <div data-testid="return-path-cell">
      <StatusPill status={chip} />
      <div className="mt-0.5 text-[11px] text-gray-500" title="primary / failover region">
        {regions}
      </div>
      {dkimStatus === 'verified' && state === 'not-configured' && (
        <div className="mt-0.5 max-w-[16rem] text-[11px] leading-snug text-amber-700 dark:text-amber-300">
          DKIM verified but the Return-Path was never applied - mail goes out
          with an amazonses.com envelope sender (spam risk). Recheck re-triggers
          it once the customer has published the bounces records.
        </div>
      )}
    </div>
  );
}

function RecentSendsCard({
  sends,
  customerId,
}: {
  sends: NonNullable<CustomerDetail['recent_sends']> | null;
  customerId: string;
}) {
  return (
    <CardShell
      title="Recent sends"
      subtitle={sends ? `last ${sends.length}` : undefined}
      right={
        <Link
          to={`/oms/sends?customer=${customerId}`}
          className="text-xs text-brand-500 hover:underline"
        >
          View all →
        </Link>
      }
    >
      {!sends || sends.length === 0 ? (
        <p className="text-gray-500">No recent sends.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1">Sent</th>
              <th className="py-1">Subject</th>
              <th className="py-1">From / To</th>
              <th className="py-1">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {sends.map((s) => (
              <tr key={s.id}>
                <td className="whitespace-nowrap py-1.5 tabular-nums text-gray-500">
                  {s.sent_at ? formatDate(s.sent_at) : '—'}
                </td>
                <td className="truncate py-1.5">{s.subject ?? '—'}</td>
                <td className="py-1.5 text-xs text-gray-600 dark:text-gray-400">
                  <div className="truncate">{s.from ?? '—'}</div>
                  <div className="truncate text-gray-400">→ {s.to ?? '—'}</div>
                </td>
                <td className="py-1.5">
                  <StatusPill status={s.status ?? null} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </CardShell>
  );
}

function SuppressionCard({
  items,
  customerId,
  onRemove,
}: {
  items: NonNullable<CustomerDetail['suppression']> | null;
  customerId: string;
  onRemove: (id: string) => Promise<boolean>;
}) {
  const [confirming, setConfirming] = useState<{ id: string; email: string } | null>(null);
  return (
    <CardShell
      title="Suppression"
      subtitle={items ? `top ${items.length}` : undefined}
      right={
        <Link
          to={`/oms/suppression?customer=${customerId}`}
          className="text-xs text-brand-500 hover:underline"
        >
          View all →
        </Link>
      }
    >
      {!items || items.length === 0 ? (
        <p className="text-gray-500">No suppressed addresses.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1">Email</th>
              <th className="py-1">Reason</th>
              <th className="py-1">Added</th>
              <th className="py-1">By</th>
              <th className="py-1 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {items.map((it) => (
              <tr key={it.id}>
                <td className="py-1.5">{it.email ?? '—'}</td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">{it.reason ?? '—'}</td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">
                  {it.added_at ? formatDate(it.added_at) : '—'}
                </td>
                <td className="py-1.5 text-gray-600 dark:text-gray-400">{it.added_by ?? '—'}</td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => setConfirming({ id: it.id, email: it.email ?? it.id })}
                    className="text-xs text-red-700 dark:text-red-300 hover:underline"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {confirming && (
        <ConfirmModal
          title="Remove suppression"
          message={`Allow sending to ${confirming.email} again. Use only after confirming this was a false positive — we will re-flag bouncing addresses on the next attempt. Continue?`}
          confirmLabel="Remove"
          confirmTone="red"
          onCancel={() => setConfirming(null)}
          onConfirm={async () => {
            const ok = await onRemove(confirming.id);
            if (ok) setConfirming(null);
          }}
        />
      )}
    </CardShell>
  );
}

// -------- shared --------

export function UnconfiguredEmptyState() {
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-6">
      <h2 className="text-base font-semibold text-amber-900 dark:text-amber-200">orboto Mail bridge not configured</h2>
      <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
        Set <code>OMS_API_BASE</code> + <code>OMS_INTERNAL_HMAC_SECRET</code> on the
        orboto Control Coolify project and redeploy.
      </p>
    </div>
  );
}

export function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const tone =
    status === 'active' || status === 'verified' || status === 'delivered'
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
      : status === 'suspended' ||
          status === 'bounced' ||
          status === 'rejected' ||
          status === 'failed' ||
          status === 'complained'
        ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800'
        : status === 'pending' || status === 'queued'
          ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800'
          : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
  return (
    <span className={`inline-flex rounded border px-2 py-0.5 text-xs ${tone}`}>{status}</span>
  );
}

export function formatDate(iso: string): string {
  try {
    return fmtDateTime(iso);
  } catch {
    return iso;
  }
}

function ModalWrap({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel,
  submitting,
  submitLabel,
}: {
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div className="mt-2 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
      >
        {submitting ? '…' : submitLabel}
      </button>
    </div>
  );
}

function ConfirmModal({
  title,
  message,
  confirmLabel,
  confirmTone,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  confirmTone: 'red' | 'amber' | 'brand';
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  async function fire() {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }
  const cls =
    confirmTone === 'red'
      ? 'bg-red-600 hover:bg-red-700'
      : confirmTone === 'amber'
        ? 'bg-amber-500 hover:bg-amber-600'
        : 'bg-brand-500 hover:bg-brand-600';
  return (
    <ModalWrap title={title} onClose={onCancel}>
      <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void fire()}
          disabled={submitting}
          className={`rounded px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400 ${cls}`}
        >
          {submitting ? '…' : confirmLabel}
        </button>
      </div>
    </ModalWrap>
  );
}
